/* Shared dispatch/arrival policy. Uses calendar arithmetic, never guessed carrier transit. */
(() => {
  const dayKey = (date) => date.toISOString().slice(0, 10);
  const parseDay = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
    const date = new Date(value + "T00:00:00Z");
    return Number.isFinite(date.valueOf()) && dayKey(date) === value
      ? date
      : null;
  };
  const businessDay = (date, holidays) =>
    ![0, 6].includes(date.getUTCDay()) && !holidays.has(dayKey(date));
  const addBusinessDays = (value, count, holidays = new Set()) => {
    const date = new Date(value);
    for (
      let remaining = count, limit = 0;
      remaining > 0 && limit < 800;
      limit++
    ) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (businessDay(date, holidays)) remaining--;
    }
    return date;
  };
  const subtractBusinessDays = (value, count, holidays = new Set()) => {
    const date = new Date(value);
    for (
      let remaining = count, limit = 0;
      remaining > 0 && limit < 800;
      limit++
    ) {
      date.setUTCDate(date.getUTCDate() - 1);
      if (businessDay(date, holidays)) remaining--;
    }
    return date;
  };
  const storeClock = (now, timezone) => {
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(now)
          .map((p) => [p.type, p.value]),
      );
      return {
        day: parseDay(`${parts.year}-${parts.month}-${parts.day}`),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
      };
    } catch {
      return null;
    }
  };
  const integer = (value, fallback, max = 60) =>
    /^\d+$/.test(String(value)) && Number(value) <= max
      ? Number(value)
      : fallback;
  function estimate(data, now = new Date()) {
    if (
      data.selectionUnavailable === "true" ||
      data.available !== "true" ||
      data.policy === "discontinued" ||
      data.backorder === "true" ||
      data.shippable === "false"
    )
      return {};
    const clock = storeClock(now, data.timezone || "America/Los_Angeles");
    if (!clock?.day) return {};
    const holidays = new Set(
      (data.holidays || "").split(/[\s,]+/).filter((value) => parseDay(value)),
    );
    const future = (value, today = clock.day) => {
      const date = parseDay(value);
      return date && date >= today ? date : null;
    };
    if (data.policy === "pickup-only")
      return { pickup: future(data.pickupDate) };
    let base = clock.day;
    if (
      !businessDay(base, holidays) ||
      clock.hour >= integer(data.cutoff, 17, 23)
    )
      base = addBusinessDays(base, 1, holidays);
    const min = integer(data.dispatchMin, 1),
      max = Math.max(min, integer(data.dispatchMax, min));
    const dispatch = [
      addBusinessDays(base, min, holidays),
      addBusinessDays(base, max, holidays),
    ];
    const result = { dispatch };
    const suppliedShipping = future(data.shippingDate);
    if (suppliedShipping)
      result.shipping = [suppliedShipping, suppliedShipping];
    else if (data.transitEnabled === "true") {
      const tmin = integer(data.transitMin, null),
        tmax = integer(data.transitMax, null);
      if (tmin !== null && tmax !== null && tmax >= tmin)
        result.shipping = [
          addBusinessDays(dispatch[0], tmin, holidays),
          addBusinessDays(dispatch[1], tmax, holidays),
        ];
    }
    // The account-aware selector renders local delivery using the merchant's
    // ZIP/cutoff estimate or the optional verified-stock service.
    result.pickup = future(data.pickupDate);
    if (data.policy === "preorder" && !result.pickup) {
      const offset = Math.max(1, integer(data.specialPickupOffset, 2, 2));
      const earliestReady = addBusinessDays(base, 1, holidays);
      result.pickupWindow = (result.shipping || dispatch).map((date) => {
        const ready = subtractBusinessDays(date, offset, holidays);
        return ready < earliestReady ? new Date(earliestReady) : ready;
      });
    }
    return result;
  }
  if (typeof module !== "undefined" && module.exports)
    module.exports = {
      parseDay,
      dayKey,
      addBusinessDays,
      subtractBusinessDays,
      storeClock,
      estimate,
    };
  if (typeof window === "undefined" || !window.customElements) return;
  const format = (date) =>
    new Intl.DateTimeFormat(document.documentElement.lang || "en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(date);
  const range = (dates) =>
    dayKey(dates[0]) === dayKey(dates[1])
      ? format(dates[0])
      : `${format(dates[0])} – ${format(dates[1])}`;
  class SaberFulfillment extends HTMLElement {
    connectedCallback() {
      this.original ||= Object.fromEntries(
        ["shipping", "local", "pickup"].map((key) => [
          key,
          this.querySelector(`[data-fulfillment-${key}]`)?.innerHTML || "",
        ]),
      );
      this.refresh();
    }
    disconnectedCallback() {
      this.quantityObserver?.disconnect();
      this.quantitySource = null;
      this.productObserver?.disconnect();
      this.productSource = null;
    }
    updateRequestedQuantity() {
      const minimum = Math.max(
        1,
        integer(this.dataset.minimumQuantity, 1, Number.MAX_SAFE_INTEGER),
      );
      this.dataset.requestedQuantity = String(minimum);
      const product = this.closest(".ss-pdp-fulfillment-wrap")?.closest(
        ".ss-pdp",
      );
      if (product !== this.productSource) {
        this.productObserver?.disconnect();
        this.productSource = product;
        if (product) {
          this.productObserver = new MutationObserver(() => {
            this.updateRequestedQuantity();
            const local = this.querySelector("[data-fulfillment-local]");
            if (local) local.innerHTML = this.original.local;
            this.refreshLocalDelivery();
          });
          this.productObserver.observe(product, {
            attributes: true,
            attributeFilter: [
              "data-variant-loading",
              "data-selection-unavailable",
            ],
          });
        }
      }
      if (!product) return;
      if (
        product.dataset.variantLoading === "true" ||
        product.dataset.selectionUnavailable === "true"
      ) {
        this.dataset.requestedQuantity = "0";
        return;
      }
      const section =
        product.dataset.sectionId || product.id.replace(/^ProductPage-/, "");
      const form = document.getElementById(`product-form-${section}`);
      if (!form || !product.contains(form)) return;
      const source = form.querySelector("product-quantity-rules");
      if (source !== this.quantitySource) {
        this.quantityObserver?.disconnect();
        this.quantitySource = source;
        if (source) {
          this.quantityObserver = new MutationObserver(() => this.refresh());
          this.quantityObserver.observe(source, {
            attributes: true,
            attributeFilter: ["data-cart-quantity"],
          });
        }
      }
      const input = form.elements.namedItem("quantity");
      const quantity = input?.value || "";
      const cartQuantity = source?.dataset.cartQuantity ?? "0";
      const whole = (value) =>
        /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value));
      const requested = Number(quantity) + Number(cartQuantity);
      if (
        String(form.elements.namedItem("id")?.value) !==
          this.dataset.variantId ||
        !whole(quantity) ||
        Number(quantity) < 1 ||
        !whole(cartQuantity) ||
        !Number.isSafeInteger(requested) ||
        (input?.type !== "hidden" && !input?.checkValidity()) ||
        (source?.quantityIsValid && !source.quantityIsValid())
      )
        this.dataset.requestedQuantity = "0";
      else
        this.dataset.requestedQuantity = String(Math.max(minimum, requested));
    }
    refreshLocalDelivery() {
      try {
        window.SaberDelivery?.refreshElement(this)?.catch?.(() => {});
      } catch {
        // An unavailable delivery service must not interrupt native shipping/pickup.
      }
    }
    refresh() {
      this.updateRequestedQuantity();
      const selectionUnavailable = this.dataset.selectionUnavailable === "true";
      const selectionError = this.querySelector(
        "[data-fulfillment-selection-error]",
      );
      const options = this.querySelector("[data-fulfillment-options]");
      if (selectionError) selectionError.hidden = !selectionUnavailable;
      if (options) options.hidden = selectionUnavailable;
      const local = this.querySelector("[data-fulfillment-local]");
      if (local) local.innerHTML = this.original.local;
      if (selectionUnavailable) {
        this.refreshLocalDelivery();
        return;
      }
      const result = estimate(this.dataset);
      const set = (key, text) => {
        const node = this.querySelector(`[data-fulfillment-${key}]`);
        if (node) node.textContent = text;
      };
      for (const key of ["shipping", "local", "pickup"]) {
        const node = this.querySelector(`[data-fulfillment-${key}]`);
        if (node) node.innerHTML = this.original[key];
      }
      if (result.shipping)
        set("shipping", `Est. delivery ${range(result.shipping)}`);
      else if (result.dispatch)
        set("shipping", `Est. dispatch ${range(result.dispatch)}`);
      if (result.pickup) set("pickup", `Est. ready ${format(result.pickup)}`);
      else if (result.pickupWindow)
        set("pickup", `Est. ready ${range(result.pickupWindow)}`);
      this.refreshLocalDelivery();
    }
  }
  if (!customElements.get("saber-fulfillment"))
    customElements.define("saber-fulfillment", SaberFulfillment);
  const refresh = () => {
    if (!document.hidden)
      document
        .querySelectorAll("saber-fulfillment")
        .forEach((element) => element.refresh());
  };
  const refreshProduct = (event) => {
    if (["input", "change"].includes(event.type)) {
      if (!event.target.matches?.('[name="quantity"]')) return;
      const product = event.target.closest(".ss-pdp");
      product
        ?.querySelectorAll(".ss-pdp-fulfillment-wrap > saber-fulfillment")
        .forEach((element) => element.refresh());
    } else
      document
        .querySelectorAll(".ss-pdp-fulfillment-wrap > saber-fulfillment")
        .forEach((element) => element.refresh());
  };
  for (const event of [
    "input",
    "change",
    "product:quantity-update",
    "product:variant-change",
    "cart:item-add",
    "saber:cart-updated",
  ])
    document.addEventListener(event, refreshProduct);
  let refreshTimer;
  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    // Align with the clock minute so 11:59 never waits a full interval to roll over.
    refreshTimer = window.setTimeout(
      () => {
        refresh();
        scheduleRefresh();
      },
      60000 - (Date.now() % 60000) + 5,
    );
  };
  document.addEventListener("visibilitychange", () => {
    refresh();
    scheduleRefresh();
  });
  scheduleRefresh();
})();
